import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCreateProject } from '@/api/project/use-create-project.ts';
import FormInput from '@/components/form/form-input/form-input.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const createProjectSchema = z.object({
  name: z
    .string({ error: 'Project name must be a string' })
    .min(1, 'Project name is required')
    .max(100, 'Project name must be less than 100 characters'),
});

type CreateProjectForm = z.infer<typeof createProjectSchema>;

type CreateProjectModalProps = {
  onClose: () => void;
};

const CreateProjectModal = ({ onClose }: CreateProjectModalProps) => {
  const form = useForm<CreateProjectForm>({
    resolver: zodResolver(createProjectSchema),
  });

  const { mutateAsync: createProject, isPending } = useCreateProject();

  const onSubmit = (data: CreateProjectForm) => {
    createProject(data)
      .then(() => {
        form.reset();
        onClose();
      })
      .catch(() => undefined);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              You can create a new project here and add members and expenses later in project
              settings.
            </DialogDescription>
          </DialogHeader>
          <FormInput name="name" label="Name" />
          <DialogFooter>
            <Button onClick={() => onClose()} variant="outline">
              Cancel
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type="submit">
              Create project
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};

export default CreateProjectModal;
